import { useState, useEffect } from 'react';
import { updateLiteratureFeedback } from './apiController';
import SelectDocumentModal from './SelectDocumentModal';
import { CustomButton, showToaster, toasterTypes } from '../../widgets';

export default function ViewArticle(props) {
  const { article, topicId, query } = props;
  const [activeTab, setActiveTab] = useState('article');
  const [rating, setRating] = useState(article?.article_feedback?.rating || 0);
  const [comment, setComment] = useState(article?.article_feedback?.comment || "");
  const [submitting, setSubmitting] = useState(false);
  const [feedbackExists, setFeedbackExists] = useState(!!article?.article_feedback?.rating);
  const [isEditing, setIsEditing] = useState(!feedbackExists);

  useEffect(() => {
    setRating(article?.article_feedback?.rating || 0);
    setComment(article?.article_feedback?.comment || "");
    const exists = !!article?.article_feedback?.rating;
    setFeedbackExists(exists);
    if (exists && !isEditing) {
      setIsEditing(false);
    }
  }, [article]);

  const [showSectionModal, setShowSectionModal] = useState(false);

  if (!article) {
    return (
      <div className="view-article">
        <div className="close-button" onClick={() => props.onClose()}>
          <span>×</span> Close
        </div>
        <div className="no-article">No article selected</div>
      </div>
    );
  }

  const handleAddToReference = (selection) => {
    try {
      const documentId = selection.docselected.value;
      const documentName = selection.docselected.label;

      const storageKey = `${documentId}_references`;
      const storedRefs = JSON.parse(localStorage.getItem(storageKey) || '[]');

      const newReference = {
        id: Date.now().toString(),
        authors: article.authors ? article.authors.join(', ') : '',
        year: article.year || '',
        title: article.title || '',
        type: 'article',
        journal: article.journal || '',
        volume: article.volume || '',
        pages: article.pages || '',
        doi: article.doi && article.doi !== "No DOI" ? article.doi : '',
        url: article.full_text || '',
        isCited: false,
        createdAt: new Date().toISOString(),
      };

      const updatedRefs = [...storedRefs, newReference].map((ref, index) => ({
        ...ref,
        number: index + 1
      }));

      localStorage.setItem(storageKey, JSON.stringify(updatedRefs));
      localStorage.setItem('isChanged', 'true');

      // Dispatch event to notify ReferenceManager or other components
      window.dispatchEvent(new Event('referencesUpdated'));

      showToaster(toasterTypes.SUCCESS, `Reference added to "${documentName}" successfully!`);
      setShowSectionModal(false);
    } catch (error) {
      console.error("Error adding reference:", error);
      showToaster(toasterTypes.ERROR, "Failed to add reference");
    }
  };

  // Access relevance check data if available
  const relevanceCheck = article.relevance_check

  const normalizeRelevanceScore = (score) => {
    if (score === null || score === undefined) return 0;
    const numScore = Number(score);
    if (numScore > 0 && numScore < 1) {
      return Math.round(numScore * 100);
    }
    return Math.round(numScore);
  };

  const displayScore = normalizeRelevanceScore(relevanceCheck?.relevance_score);

  const handleSubmitFeedback = async () => {
    if (rating === 0) {
      showToaster(toasterTypes.WARNING, "Please provide a rating between 1 and 5");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        topic_id: topicId,
        query: query,
        doi: article.doi,
        article_feedback: {
          rating: Number(rating),
          comment: comment
        }
      };
      const response = await updateLiteratureFeedback(payload);
      if (response && response.message === "OK") {
        showToaster(toasterTypes.SUCCESS, "Feedback submitted successfully");
        setFeedbackExists(true);
        setIsEditing(false);
        if (props.onFeedbackUpdate) {
          props.onFeedbackUpdate();
        }
      } else {
        showToaster(toasterTypes.ERROR, "Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      showToaster(toasterTypes.ERROR, "An error occurred while submitting feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="view-article-wrapper">
      {showSectionModal && (
        <SelectDocumentModal
          onSelect={handleAddToReference}
          onClose={() => setShowSectionModal(false)}
        />
      )}
      <div className="view-article-header">
        <div className="view-article-title">
          <h2>View Article</h2>
        </div>
        <div className="view-article-close">
          <i className="icon icon-close" onClick={() => props.onClose()} />
        </div>
      </div>

      <div className="view-article-tabs">
        <button
          className={`tab-button ${activeTab === 'article' ? 'active' : ''}`}
          onClick={() => setActiveTab('article')}
        >
          Article Details
        </button>
        <button
          className={`tab-button ${activeTab === 'relevance' ? 'active' : ''}`}
          onClick={() => setActiveTab('relevance')}
        >
          Relevance Assessment
        </button>
      </div>

      <div className="view-article-container">
        {activeTab === 'article' ? (
          <div className="article-container">
            <h1 className="article-title">{article?.title}</h1>

            <div className="article-meta">
              <div className="authors">
                {article.authors && article.authors.join(', ')}
              </div>

              <div className="publication-info">
                <span className="journal">{article?.journal}.</span>
                <span className="year">{article?.year};</span>
                {article.doi && article.doi !== "No DOI" &&
                  <span className="doi">DOI: {article.doi}</span>
                }
              </div>

              <div className="source">Source: {article.source}</div>
            </div>

            <div className="section">
              <h2>Abstract</h2>
              <div className="abstract">{article.abstract}</div>
            </div>

            <div className="section">
              <h2>Full Text</h2>
              {article?.full_text ? (
                <a href={article?.full_text} className="fulltext-link" target="_blank" rel="noopener noreferrer">Access Full Text</a>
              ) : (
                <p className="fulltext-unavailable">No Full Text Available</p>
              )}
            </div>

            <div className="section">
              <h2>Cite this article</h2>
              <div className="citation-container">
                <div className="citation">
                  {article.authors && article.authors.join(', ')}. {article.title}. {article.journal}. {article.year}.
                  {article.doi && article.doi !== "No DOI" && ` doi: ${article.doi}`}
                </div>
                <div className="citation-actions mt-3">
                  <CustomButton
                    text="Add to Reference Manager"
                    type="primary"
                    onClick={() => setShowSectionModal(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="relevance-container">
            <div className="relevance-score-section">
              <h2>Relevance Score</h2>
              <div className="relevance-score-wrapper">
                <div className="relevance-score-circle"
                // style={{
                //   background: `conic-gradient(#0066cc ${relevanceCheck.relevance_score}%, #e0e0e0 0)`
                // }}
                >
                  <span className="relevance-score-number">{displayScore}%</span>
                </div>
              </div>
            </div>

            <div className="section">
              <h2>Reasoning</h2>
              <p className="reasoning-text">{relevanceCheck.reasoning}</p>
            </div>

            <div className="section">
              <h2>Key Findings</h2>
              <ul className="findings-list">
                {relevanceCheck?.key_findings?.map((finding, index) => (
                  <li key={`finding-${index}`}>{finding}</li>
                ))}
              </ul>
            </div>

            <div className="section">
              <h2>Limitations</h2>
              <ul className="limitations-list">
                {relevanceCheck?.limitations?.map((limitation, index) => (
                  <li key={`limitation-${index}`}>{limitation}</li>
                ))}
              </ul>
            </div>

            <div className="section">
              <h2>Suggestions</h2>
              <ul className="suggestions-list">
                {relevanceCheck?.suggestions?.map((suggestion, index) => (
                  <li key={`suggestion-${index}`}>{suggestion}</li>
                ))}
              </ul>
            </div>

            <div className="section feedback-section">
              <h2>Article Feedback</h2>
              {!isEditing ? (
                <div className="feedback-display">
                  <div className="feedback-field">
                    <label>Rating (1-5)</label>
                    <div className="rating-options">
                      <div className="rating-buttons">
                        {[1, 2, 3, 4, 5].map((num) => (
                          <button
                            key={num}
                            className={`rating-btn readonly ${Number(rating) === num ? 'selected' : ''}`}
                            disabled
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="feedback-field">
                    <label>Comments</label>
                    <div className="feedback-comment-view">
                      {comment || <span className="no-comment">No comments provided</span>}
                    </div>
                  </div>
                  <div className="feedback-actions">
                    <CustomButton
                      text="Edit Feedback"
                      type="primary"
                      onClick={() => setIsEditing(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="feedback-form">
                  <div className="feedback-field">
                    <label>Rating (1-5)</label>
                    <div className="rating-options">
                      <div className="rating-buttons">
                        {[1, 2, 3, 4, 5].map((num) => (
                          <button
                            key={num}
                            className={`rating-btn ${Number(rating) === num ? 'selected' : ''}`}
                            onClick={() => setRating(num)}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="feedback-field">
                    <label>Comments</label>
                    <textarea
                      className="feedback-textarea"
                      placeholder="Enter your comments here..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={4}
                    />
                  </div>
                  <div className="feedback-submit">
                    {feedbackExists && (
                      <CustomButton
                        text="Cancel"
                        type="secondary"
                        onClick={() => {
                          setIsEditing(false);
                          setRating(article?.article_feedback?.rating || 0);
                          setComment(article?.article_feedback?.comment || "");
                        }}
                        isDisabled={submitting}
                        customClass="margin-right-10"
                      />
                    )}
                    <CustomButton
                      text={submitting ? "Submitting..." : (feedbackExists ? "Update Feedback" : "Submit Feedback")}
                      type="primary"
                      onClick={handleSubmitFeedback}
                      isDisabled={submitting}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}